import { useEffect, useRef, useState } from "react";
import {
  GraduationCap, Loader2, Sparkles, UploadCloud, Trash2, Brain, MessageSquareQuote,
  HelpCircle, Shield, Zap, RefreshCw, Clock,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const KIND_META = {
  faq: { label: "Buyer Q&A", icon: HelpCircle, color: "#2F6F5B", bg: "#E8F3EE" },
  objection: { label: "Objection Handling", icon: Shield, color: "#9A5B1E", bg: "#F7ECDD" },
  style: { label: "Voice & Style", icon: MessageSquareQuote, color: "#6A4BA8", bg: "#EEE8F7" },
};

const relTime = (iso) => {
  if (!iso) return "never";
  const d = new Date(iso);
  return d.toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
};

// Docstate — Sara's self-evolving training centre.
// Shows the evolving learnings, lets the team upload WhatsApp chats and trigger
// on-demand training. Nightly auto-training runs at 3am Dubai time.
export const ReTrainingTab = () => {
  const [data, setData] = useState(null);
  const [training, setTraining] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/realestate/training");
      setData(data);
    } catch {
      /* quiet */
    }
  };
  useEffect(() => { load(); }, []);

  const trainNow = async () => {
    setTraining(true);
    try {
      await api.post("/admin/realestate/training/run", {});
      toast.success("Training started — Sara is learning from recent chats. Refresh in a minute to see new learnings.");
      setTimeout(load, 8000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not start training");
    } finally {
      setTraining(false);
    }
  };

  const seed = async () => {
    setSeeding(true);
    try {
      const { data: r } = await api.post("/admin/realestate/training/seed", {});
      toast.success(r.added ? `Loaded ${r.added} expert starter learnings` : "Starter pack already loaded");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load starter pack");
    } finally {
      setSeeding(false);
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("File too large — keep exports under 5MB (export without media)"); return; }
    setUploading(true);
    try {
      const content = await file.text();
      const format = file.name.toLowerCase().endsWith(".csv") ? "csv" : "txt";
      const { data: r } = await api.post(
        "/admin/realestate/training/upload",
        { filename: file.name, content, format },
        { timeout: 120000 },
      );
      toast.success(`Learned from ${r.turns} messages — ${r.added} new ${r.added === 1 ? "insight" : "insights"} added`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (lid) => {
    try {
      await api.delete(`/admin/realestate/training/${lid}`);
      setData((d) => ({ ...d, learnings: d.learnings.filter((l) => l.id !== lid) }));
      toast.success("Removed");
    } catch {
      toast.error("Could not remove");
    }
  };

  if (!data) return <div className="flex min-h-[40vh] items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#C9A24B]" /></div>;

  const { counts = {}, total = 0, meta = {}, learnings = [] } = data;

  return (
    <div className="space-y-7" data-testid="re-training-tab">
      {/* Header */}
      <div className="glass rounded-3xl p-7">
        <div className="flex flex-wrap items-center gap-4">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-[#C9A24B] to-[#8a6d1f] shadow-[0_8px_24px_rgba(201,162,75,0.25)]">
            <GraduationCap className="h-6 w-6 text-white" aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="font-serif text-2xl font-semibold text-[#1A1615]">Sara — Self-Evolving Training</h2>
            <p className="mt-1 text-sm font-light leading-relaxed text-[#91837C]">
              Sara gets sharper every day. She learns from your live and human-handled chats, plus any WhatsApp
              conversations you upload — mirroring how your best agents talk and close.
            </p>
          </div>
          <button onClick={trainNow} disabled={training} data-testid="re-training-train-now" className="btn-gold inline-flex items-center gap-2 px-6 py-3 text-xs disabled:opacity-60">
            {training ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />} Train Now
          </button>
        </div>

        {/* Status strip */}
        <div className="mt-6 grid gap-3 sm:grid-cols-3">
          <div className="rounded-2xl border border-[#EBE3DA] bg-white/60 p-4" data-testid="re-training-stat-auto">
            <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#B38B2D]"><Clock className="h-3.5 w-3.5" /> Auto-training</p>
            <p className="mt-1.5 text-sm font-semibold text-[#1A1615]">Nightly · 3:00 AM Dubai</p>
          </div>
          <div className="rounded-2xl border border-[#EBE3DA] bg-white/60 p-4" data-testid="re-training-stat-last">
            <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#B38B2D]"><RefreshCw className="h-3.5 w-3.5" /> Last trained</p>
            <p className="mt-1.5 text-sm font-semibold text-[#1A1615]">{relTime(meta.last_run_at)}{meta.last_added != null ? ` · +${meta.last_added}` : ""}</p>
          </div>
          <div className="rounded-2xl border border-[#EBE3DA] bg-white/60 p-4" data-testid="re-training-stat-total">
            <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#B38B2D]"><Brain className="h-3.5 w-3.5" /> Learnings</p>
            <p className="mt-1.5 text-sm font-semibold text-[#1A1615]">{total} live insights</p>
          </div>
        </div>
      </div>

      {/* Upload + starter pack */}
      <div className="grid gap-7 lg:grid-cols-2">
        <div className="glass rounded-3xl p-7" data-testid="re-training-upload">
          <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#B38B2D]"><UploadCloud className="h-3.5 w-3.5" /> Upload WhatsApp chats</p>
          <p className="mt-2 text-sm font-light leading-relaxed text-[#91837C]">
            Export a chat from WhatsApp (<b>⋮ → More → Export chat → Without media</b>) and drop the <code className="rounded bg-[#F8F3E6] px-1 text-[#8a6d1f]">.txt</code> here.
            Sara studies how your humans talk and closes — her style and answers improve instantly. CSV also supported.
          </p>
          <input ref={fileRef} type="file" accept=".txt,.csv,text/plain,text/csv" className="hidden" onChange={onFile} data-testid="re-training-file-input" />
          <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="re-training-upload-btn" className="btn-outline-lux mt-4 inline-flex items-center gap-2 px-5 py-2.5 text-xs disabled:opacity-60">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} {uploading ? "Learning…" : "Choose chat file"}
          </button>
        </div>

        <div className="glass rounded-3xl p-7" data-testid="re-training-starter">
          <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#B38B2D]"><Sparkles className="h-3.5 w-3.5" /> Expert starter pack</p>
          <p className="mt-2 text-sm font-light leading-relaxed text-[#91837C]">
            Load a curated set of Dubai-consultant Q&amp;A, objection handling and voice examples so Sara starts sharp —
            then she keeps evolving from your real conversations on top.
          </p>
          <button onClick={seed} disabled={seeding} data-testid="re-training-seed-btn" className="btn-outline-lux mt-4 inline-flex items-center gap-2 px-5 py-2.5 text-xs disabled:opacity-60">
            {seeding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Load starter pack
          </button>
        </div>
      </div>

      {/* Learnings list */}
      <div className="glass rounded-3xl p-7" data-testid="re-training-learnings">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-serif text-lg font-semibold text-[#1A1615]">What Sara has learned</h3>
          <div className="flex flex-wrap gap-2">
            {Object.entries(KIND_META).map(([k, m]) => (
              <span key={k} className="rounded-full px-3 py-1 text-[11px] font-semibold" style={{ background: m.bg, color: m.color }}>
                {m.label}: {counts[k] || 0}
              </span>
            ))}
          </div>
        </div>
        <p className="mt-1 text-[11px] font-light text-[#91837C]">These are injected live into every conversation. Remove anything off-brand and Sara stops using it.</p>

        <div className="mt-5 space-y-3">
          {learnings.map((l) => {
            const m = KIND_META[l.kind] || KIND_META.faq;
            const Icon = m.icon;
            return (
              <div key={l.id} className="flex items-start gap-3 rounded-2xl border border-[#EBE3DA] bg-white/60 p-4" data-testid={`re-training-item-${l.id}`}>
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full" style={{ background: m.bg }}>
                  <Icon className="h-4 w-4" style={{ color: m.color }} />
                </span>
                <div className="min-w-0 flex-1">
                  {l.question && <p className="text-sm font-semibold text-[#1A1615]">{l.question}</p>}
                  <p className="mt-0.5 text-sm font-light leading-relaxed text-[#5A504B]">{l.answer}</p>
                  <p className="mt-1.5 text-[10px] uppercase tracking-[0.12em] text-[#B0A197]">{m.label} · via {l.source}{l.source_label ? ` · ${l.source_label}` : ""}</p>
                </div>
                <button onClick={() => remove(l.id)} aria-label="Remove" data-testid={`re-training-delete-${l.id}`} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[#C9B8A8] hover:text-red-500">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            );
          })}
          {!learnings.length && (
            <div className="rounded-2xl border border-dashed border-[#EBE3DA] p-8 text-center">
              <p className="text-sm font-light text-[#91837C]">Nothing learned yet. Load the starter pack, upload a chat, or hit <b>Train Now</b> to begin.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
